import { Howl, Howler } from 'howler';

// Types
interface SoundLibrary {
  [key: string]: Howl;
}

class SoundManagerClass {
  private sounds: SoundLibrary = {};
  private ambientSound: Howl | null = null;
  private enabled = true;

  constructor() {
    // Pre-define sound URLs with more reliable hosting
    this.sounds = {
      keypress: new Howl({
        src: ['https://cdn.freesound.org/previews/242/242429_4127097-lq.mp3'],
        volume: 0.2,
        preload: true,
        format: ['mp3'],
        html5: true, // This helps with MIME issues
        onloaderror: (_id, error) => {
          console.warn('Sound loading error (keypress):', error);
        }
      }),
      command: new Howl({
        src: ['https://cdn.freesound.org/previews/242/242430_4127097-lq.mp3'],
        volume: 0.3,
        preload: true,
        format: ['mp3'],
        html5: true,
        onloaderror: (_id, error) => {
          console.warn('Sound loading error (command):', error);
        }
      }),
      toggle: new Howl({
        src: ['https://cdn.freesound.org/previews/521/521639_7780892-lq.mp3'],
        volume: 0.4,
        preload: true,
        format: ['mp3'],
        html5: true,
        onloaderror: (_id, error) => {
          console.warn('Sound loading error (toggle):', error);
        }
      }),
      error: new Howl({
        src: ['https://cdn.freesound.org/previews/404/404754_7015629-lq.mp3'],
        volume: 0.4,
        preload: true,
        format: ['mp3'],
        html5: true,
        onloaderror: (_id, error) => {
          console.warn('Sound loading error (error):', error);
        }
      }),
    };

    this.ambientSound = new Howl({
      src: ['https://cdn.freesound.org/previews/249/249033_3797507-lq.mp3'],
      loop: true,
      volume: 0.2,
      preload: true,
      format: ['mp3'],
      html5: true,
      onloaderror: (_id, error) => {
        console.warn('Sound loading error (ambient):', error);
      }
    });
  }

  initialize(enabled: boolean): void {
    this.enabled = enabled;
    
    // Set global volume
    Howler.volume(0.5);
    
    // Configure Howler to use HTML5 Audio
    (Howler as unknown as { html5: boolean }).html5 = true;
    
    // Remove the problematic Howler.on call that was causing errors
  }

  playSound(name: string): void {
    if (!this.enabled) return;
    
    const sound = this.sounds[name];
    if (sound) {
      try {
        sound.play();
      } catch (err) {
        console.warn('Error playing sound:', err);
      }
    }
  }

  playAmbient(): void {
    if (!this.enabled || !this.ambientSound) return;
    
    // Make sure ambient sound is always playing
    try {
      if (!this.ambientSound.playing()) {
        this.ambientSound.play();
      }
    } catch (err) {
      console.warn('Error playing ambient sound:', err);
    }
  }

  stopAmbient(): void {
    if (this.ambientSound && this.ambientSound.playing()) {
      this.ambientSound.pause();
    }
  }

  stopAll(): void {
    try {
      Howler.stop();
    } catch (err) {
      console.warn('Error stopping sounds:', err);
    }
  }

  setVolume(volume: number): void {
    Howler.volume(volume);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      this.playAmbient();
    }
  }
}

// Create singleton instance
const SoundManager = new SoundManagerClass();

export default SoundManager;